import { useState, useEffect, useCallback, useMemo } from "react";
import { Chapter, Language } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Plus,
  Trash,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Loader2,
  Clock,
} from "lucide-react";
import MDEditor from "@uiw/react-md-editor";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Add theme detector hook
const useThemeDetector = () => {
  // Default to light theme
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
};

// Function to convert seconds to HH:MM:SS format
const secondsToTimeString = (seconds: number | undefined): string => {
  if (seconds === undefined || seconds === null) return "00:00:00";

  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  return [
    hours.toString().padStart(2, "0"),
    minutes.toString().padStart(2, "0"),
    secs.toString().padStart(2, "0"),
  ].join(":");
};

// Function to convert HH:MM:SS format to seconds
const timeStringToSeconds = (timeString: string): number => {
  // Validate format - should be HH:MM:SS
  if (!/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
    // Try to parse from partial formats
    if (/^\d{2}:\d{2}$/.test(timeString)) {
      // MM:SS format - treat as minutes:seconds, not hours:minutes
      const [minutes, seconds] = timeString.split(":").map(Number);
      return minutes * 60 + seconds;
    } else {
      return 0; // Invalid format
    }
  }

  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

// Define an internal Chapter type that uses string IDs for UI operations
interface InternalChapter {
  id: string; // Always string for UI operations
  originalId: number | null; // Store the original ID if it exists
  label: string;
  title: string;
  content: string;
  startTime?: number;
  position?: number;
  language?: string;
}

// Define a type that includes the language property for type safety
interface ChapterWithExtraProps extends Chapter {
  language?: string;
}

interface SortableChapterItemProps {
  chapter: InternalChapter;
  onUpdateChapter: (
    chapterId: string,
    field: keyof InternalChapter,
    value: string | number
  ) => void;
  onRemoveChapter: (chapterId: string) => void;
  isExpanded: boolean;
  onToggleExpand: (chapterId: string) => void;
}

function SortableChapterItem({
  chapter,
  onUpdateChapter,
  onRemoveChapter,
  isExpanded,
  onToggleExpand,
}: SortableChapterItemProps) {
  const theme = useThemeDetector();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chapter.id });

  const [timeValue, setTimeValue] = useState(
    secondsToTimeString(chapter.startTime)
  );

  useEffect(() => {
    setTimeValue(secondsToTimeString(chapter.startTime));
  }, [chapter.startTime]);

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTimeValue(newValue);
    if (/^\d{2}:\d{2}:\d{2}$/.test(newValue)) {
      // Only update the actual chapter when we have a valid time format
      const seconds = timeStringToSeconds(newValue);
      onUpdateChapter(chapter.id, "startTime", seconds);
    }
  };

  // Add new key press handler to restrict input
  const handleTimeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only allow numbers and colons
    if (
      !/[0-9:]/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Delete" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "Tab"
    ) {
      e.preventDefault();
    }

    // Automatically add colons after hours and minutes
    const input = e.currentTarget;
    const value = input.value;

    if (e.key >= "0" && e.key <= "9") {
      // If we're at position 2 and no colon exists, add colon after hours
      if (
        input.selectionStart === 2 &&
        input.selectionEnd === 2 &&
        !value.includes(":")
      ) {
        e.preventDefault();
        setTimeValue(value.substring(0, 2) + ":" + e.key);
        // Set cursor position after the newly inserted character
        setTimeout(() => {
          input.selectionStart = 4;
          input.selectionEnd = 4;
        }, 0);
      }
      // If we're at position 5 and only one colon exists, add colon after minutes
      else if (
        input.selectionStart === 5 &&
        input.selectionEnd === 5 &&
        (value.match(/:/g) || []).length === 1
      ) {
        e.preventDefault();
        setTimeValue(value.substring(0, 5) + ":" + e.key);
        // Set cursor position after the newly inserted character
        setTimeout(() => {
          input.selectionStart = 7;
          input.selectionEnd = 7;
        }, 0);
      }
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    // Stop event propagation completely
    e.preventDefault();
    e.stopPropagation();

    // Call the parent component's toggle function
    onToggleExpand(chapter.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-md overflow-hidden mb-4 ${
        isDragging ? "opacity-50 bg-accent" : ""
      }`}
    >
      <div className="bg-muted/50 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              {chapter.label || "No label"}
              {chapter.startTime !== undefined &&
                chapter.startTime !== null && (
                  <span className="inline-flex items-center gap-0.5 text-xs bg-muted rounded px-1 ml-1">
                    <Clock className="h-3 w-3" />
                    {secondsToTimeString(chapter.startTime)}
                  </span>
                )}
            </p>
            <h3 className="text-sm font-medium">
              {chapter.title || "Untitled Chapter"}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleClick}
            className="h-8 w-8 p-0"
            type="button"
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4 border-t">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`chapter-label-${chapter.id}`}>Label</Label>
              <Input
                id={`chapter-label-${chapter.id}`}
                value={chapter.label}
                onChange={(e) =>
                  onUpdateChapter(chapter.id, "label", e.target.value)
                }
                placeholder="e.g., Introduction, Chapter 1 of 8"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`chapter-startTime-${chapter.id}`}>
                Start Time (HH:MM:SS)
              </Label>
              <div className="flex">
                <div className="relative w-full">
                  <Clock className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id={`chapter-startTime-${chapter.id}`}
                    type="text"
                    value={timeValue}
                    onChange={handleTimeChange}
                    onKeyDown={handleTimeKeyPress}
                    maxLength={8}
                    placeholder="00:00:00"
                    className="pl-8"
                    pattern="[0-9]{2}:[0-9]{2}:[0-9]{2}"
                  />
                </div>
                <div className="ml-2 text-xs text-muted-foreground self-center">
                  {chapter.startTime !== undefined && chapter.startTime !== null
                    ? `${chapter.startTime}s`
                    : "0s"}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`chapter-title-${chapter.id}`}>Title</Label>
            <Input
              id={`chapter-title-${chapter.id}`}
              value={chapter.title}
              onChange={(e) =>
                onUpdateChapter(chapter.id, "title", e.target.value)
              }
              placeholder="Chapter title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`chapter-content-${chapter.id}`}>
              Content (Markdown)
            </Label>
            <div data-color-mode={theme}>
              <MDEditor
                value={chapter.content}
                onChange={(value) =>
                  onUpdateChapter(chapter.id, "content", value || "")
                }
                height={200}
                className={theme === "dark" ? "wmde-markdown-dark" : ""}
              />
            </div>
          </div>

          <div>
            <Button
              variant="destructive"
              size="sm"
              type="button"
              onClick={() => onRemoveChapter(chapter.id)}
            >
              <Trash className="h-4 w-4 mr-1" /> Remove Chapter
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface ChapterEditorProps {
  currentChapters?: Chapter[];
  languages: Language[];
  selectedLanguage: string;
  onChaptersChange: (chapters: Chapter[]) => void;
  onSaveChapter?: (chapter: Chapter) => Promise<Chapter | null>;
  currentSummaryContent?: { id: number; [key: string]: any };
}

const ChapterEditor = ({
  currentChapters = [],
  languages,
  selectedLanguage,
  onChaptersChange,
  onSaveChapter,
  currentSummaryContent,
}: ChapterEditorProps) => {
  const theme = useThemeDetector();

  // State for internal chapter representation
  const [internalChapters, setInternalChapters] = useState<InternalChapter[]>(
    []
  );

  // Track expanded chapter IDs using a simple primitive state with array
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  // State for the new chapter time input
  const [newChapterTimeValue, setNewChapterTimeValue] = useState("00:00:00");

  // Filter chapters for the selected language using useMemo to prevent unnecessary recalculations
  const languageChapters = useMemo(() => {
    return internalChapters.filter(
      (chapter) => !chapter.language || chapter.language === selectedLanguage
    );
  }, [internalChapters, selectedLanguage]);

  // Convert the external Chapter objects to our internal format with string IDs
  const convertToInternalChapters = useCallback(
    (chapters: Chapter[]): InternalChapter[] => {
      console.log("Converting chapters to internal format:", chapters);

      return chapters.map((chapter) => {
        const internal = {
          id: `ch_${chapter.id}`, // Prefix to ensure it's a string and unique
          originalId: chapter.id,
          label: chapter.label || "",
          title: chapter.title || "",
          content: chapter.content || "",
          startTime: chapter.startTime,
          position: chapter.position,
          language:
            (chapter as ChapterWithExtraProps).language || selectedLanguage, // Use the defined interface
        };

        console.log(`Converted chapter ${chapter.id} to internal:`, internal);
        return internal;
      });
    },
    [selectedLanguage]
  );

  // Update internal chapters when external chapters change
  useEffect(() => {
    console.log(
      "ChapterEditor received chapters from parent:",
      currentChapters
    );

    if (currentChapters && currentChapters.length > 0) {
      const converted = convertToInternalChapters(currentChapters);
      console.log("Converted to internal chapters:", converted);
      setInternalChapters(converted);
    } else {
      console.log("No chapters received or empty array");
      setInternalChapters([]);
    }
  }, [currentChapters, selectedLanguage, convertToInternalChapters]);

  // When language changes or chapters are first loaded, expand only the first chapter
  useEffect(() => {
    if (languageChapters.length > 0) {
      console.log(
        "Language changed - expanding first chapter:",
        languageChapters[0].id
      );
      setExpandedIds([languageChapters[0].id]);
    } else {
      setExpandedIds([]);
    }
  }, [selectedLanguage]); // Only depend on selectedLanguage changes

  // Convert internal chapters back to the format expected by the parent
  const convertToExternalChapters = (
    chapters: InternalChapter[]
  ): Chapter[] => {
    console.log("Converting from internal to external format:", chapters);

    return chapters.map((chapter, index) => {
      // Decide what ID to use - preserve existing IDs
      let chapterId: number;

      // If chapter has an original ID from the database, use it
      if (
        chapter.originalId !== null &&
        typeof chapter.originalId === "number" &&
        chapter.originalId > 0
      ) {
        chapterId = chapter.originalId;
      } else {
        chapterId = 0; // Only use 0 for brand new chapters
      }

      const baseChapter: Chapter = {
        id: chapterId,
        label: chapter.label,
        title: chapter.title,
        content: chapter.content,
        startTime: chapter.startTime,
        position: index, // Set position based on array index to ensure proper ordering
        summary_content_id:
          currentSummaryContent?.id ||
          (currentChapters[0] || {}).summary_content_id,
      };

      // Add language property if it exists
      if (chapter.language) {
        (baseChapter as ChapterWithExtraProps).language = chapter.language;
      }

      console.log(
        `Converted internal chapter ${chapter.id} to external with ID ${baseChapter.id}`
      );
      return baseChapter;
    });
  };

  const [newChapter, setNewChapter] = useState<Partial<InternalChapter>>({
    label: "",
    title: "",
    content: "",
    startTime: 0,
    language: selectedLanguage,
  });

  // Add loading state
  const [isSaving, setIsSaving] = useState(false);

  // Simplified toggle function
  const toggleChapter = (chapterId: string) => {
    console.log("Toggle called for chapter:", chapterId);
    console.log("Current expanded IDs:", expandedIds);

    if (expandedIds.includes(chapterId)) {
      console.log("Removing from expanded list");
      setExpandedIds(expandedIds.filter((id) => id !== chapterId));
    } else {
      console.log("Adding to expanded list");
      setExpandedIds([...expandedIds, chapterId]);
    }
  };

  // Simplified expand all
  const handleExpandAll = () => {
    console.log("Expand all clicked");
    const allIds = languageChapters.map((ch) => ch.id);
    setExpandedIds(allIds);
  };

  // Simplified collapse all
  const handleCollapseAll = () => {
    console.log("Collapse all clicked");
    setExpandedIds([]);
  };

  // Inside the ChapterEditor component, add a function to check if all chapters are expanded
  const areAllExpanded = () => {
    // If there are no chapters, return false
    if (languageChapters.length === 0) return false;

    // Check if all chapter IDs are in the expandedIds array
    return languageChapters.every((chapter) =>
      expandedIds.includes(chapter.id)
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleNewTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setNewChapterTimeValue(newValue);

    if (/^\d{2}:\d{2}:\d{2}$/.test(newValue)) {
      // Convert to seconds when format is valid
      const seconds = timeStringToSeconds(newValue);
      setNewChapter({ ...newChapter, startTime: seconds });
    }
  };

  // Add new key press handler for the new chapter time input
  const handleNewTimeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only allow numbers and colons
    if (
      !/[0-9:]/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Delete" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "Tab"
    ) {
      e.preventDefault();
    }

    // Automatically add colons after hours and minutes
    const input = e.currentTarget;
    const value = input.value;

    if (e.key >= "0" && e.key <= "9") {
      // If we're at position 2 and no colon exists, add colon after hours
      if (
        input.selectionStart === 2 &&
        input.selectionEnd === 2 &&
        !value.includes(":")
      ) {
        e.preventDefault();
        setNewChapterTimeValue(value.substring(0, 2) + ":" + e.key);
        // Set cursor position after the newly inserted character
        setTimeout(() => {
          input.selectionStart = 4;
          input.selectionEnd = 4;
        }, 0);
      }
      // If we're at position 5 and only one colon exists, add colon after minutes
      else if (
        input.selectionStart === 5 &&
        input.selectionEnd === 5 &&
        (value.match(/:/g) || []).length === 1
      ) {
        e.preventDefault();
        setNewChapterTimeValue(value.substring(0, 5) + ":" + e.key);
        // Set cursor position after the newly inserted character
        setTimeout(() => {
          input.selectionStart = 7;
          input.selectionEnd = 7;
        }, 0);
      }
    }
  };

  const handleAddChapter = async () => {
    if (!newChapter.title || !newChapter.content) return;

    const newChapterId = `new_chapter_${Date.now()}`;
    const chapterToAdd: InternalChapter = {
      id: newChapterId,
      originalId: null, // This is a new chapter, no original ID
      label: newChapter.label || "Chapter",
      title: newChapter.title,
      content: newChapter.content,
      startTime: newChapter.startTime || 0,
      language: selectedLanguage,
      position: internalChapters.length, // Set position to end of array
    };

    console.log("Adding new chapter:", chapterToAdd);

    // Add new chapter to existing chapters
    const updatedInternalChapters = [...internalChapters, chapterToAdd];

    // Sort by position before updating
    const sortedChapters = [...updatedInternalChapters].sort(
      (a, b) => (a.position || 0) - (b.position || 0)
    );

    // Get only the chapters for current language before converting to external format
    const currentLanguageChapters = sortedChapters.filter(
      (ch) => ch.language === selectedLanguage || !ch.language
    );

    // Convert to external format for database saving
    const externalChapters = convertToExternalChapters(currentLanguageChapters);

    // Get the newly added chapter from the external chapters
    const newExternalChapter = externalChapters[externalChapters.length - 1];

    // If onSaveChapter is provided, save to database
    if (onSaveChapter) {
      try {
        setIsSaving(true);
        const savedChapter = await onSaveChapter(newExternalChapter);

        if (savedChapter) {
          // Update the originalId with the saved chapter ID from the database
          const updatedChapters = sortedChapters.map((ch) => {
            if (ch.id === newChapterId) {
              return {
                ...ch,
                originalId: savedChapter.id,
              };
            }
            return ch;
          });

          setInternalChapters(updatedChapters);

          // Update external chapters with the new ID
          onChaptersChange(
            convertToExternalChapters(
              updatedChapters.filter(
                (ch) => ch.language === selectedLanguage || !ch.language
              )
            )
          );
        } else {
          console.log(
            "Chapter could not be saved individually, will be saved with book"
          );
          // Even if individual save failed, update internal state so user can continue editing
          setInternalChapters(sortedChapters);
          onChaptersChange(externalChapters);
        }
      } catch (error) {
        console.error("Error saving chapter to database:", error);
        // Even if save failed, update internal state so user can continue editing
        setInternalChapters(sortedChapters);
        onChaptersChange(externalChapters);
      } finally {
        setIsSaving(false);
      }
    } else {
      // Update internal state
      setInternalChapters(sortedChapters);

      // Notify parent component
      console.log("Sending chapters to parent:", externalChapters);
      onChaptersChange(externalChapters);
    }

    // Auto-expand only the new chapter
    setExpandedIds([newChapterId]);

    // Reset form
    setNewChapter({
      label: "",
      title: "",
      content: "",
      startTime: 0,
      language: selectedLanguage,
    });
    setNewChapterTimeValue("00:00:00");
  };

  const handleRemoveChapter = (chapterId: string) => {
    const updatedInternalChapters = internalChapters.filter(
      (chapter) => chapter.id !== chapterId
    );
    setInternalChapters(updatedInternalChapters);

    // Convert to external format and notify parent - filter for current language only
    const externalChapters = convertToExternalChapters(
      updatedInternalChapters.filter(
        (ch) => ch.language === selectedLanguage || !ch.language
      )
    );
    console.log(
      "After removal - sending chapters to parent:",
      externalChapters
    );
    onChaptersChange(externalChapters);

    // Remove from expanded state
    setExpandedIds((prev) => prev.filter((id) => id !== chapterId));
  };

  const handleUpdateChapter = (
    chapterId: string,
    field: keyof InternalChapter,
    value: string | number
  ) => {
    const updatedInternalChapters = internalChapters.map((chapter) => {
      if (chapter.id === chapterId) {
        return {
          ...chapter,
          [field]: value,
        };
      }
      return chapter;
    });
    setInternalChapters(updatedInternalChapters);

    // Convert to external format and notify parent - filter for current language only
    const externalChapters = convertToExternalChapters(
      updatedInternalChapters.filter(
        (ch) => ch.language === selectedLanguage || !ch.language
      )
    );
    console.log("After update - sending chapters to parent:", externalChapters);
    onChaptersChange(externalChapters);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Get all language chapters (including those with no language)
      const langChapters = internalChapters.filter(
        (chapter) => !chapter.language || chapter.language === selectedLanguage
      );

      // Find the indexes within the language-specific chapters
      const oldIndex = langChapters.findIndex((ch) => ch.id === active.id);
      const newIndex = langChapters.findIndex((ch) => ch.id === over.id);

      // Reorder the language chapters
      const reorderedLangChapters = arrayMove(langChapters, oldIndex, newIndex);

      // Update positions based on new order
      const updatedLangChapters = reorderedLangChapters.map(
        (chapter, index) => ({
          ...chapter,
          position: index,
        })
      );

      // Combine with chapters from other languages
      const otherChapters = internalChapters.filter(
        (chapter) => chapter.language && chapter.language !== selectedLanguage
      );

      const updatedInternalChapters = [
        ...otherChapters,
        ...updatedLangChapters,
      ];
      setInternalChapters(updatedInternalChapters);

      // Convert to external format and notify parent - only send current language chapters
      const externalChapters = convertToExternalChapters(updatedLangChapters);
      console.log("After drag - sending chapters to parent:", externalChapters);
      onChaptersChange(externalChapters);
    }
  };

  const selectedLanguageName =
    languages.find((l) => l.code === selectedLanguage)?.name ||
    selectedLanguage;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>
            Chapters in {selectedLanguageName}{" "}
            <span className="text-muted-foreground text-sm">
              ({languageChapters.length})
            </span>
          </Label>

          {languageChapters.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // If all are expanded, collapse all. Otherwise, expand all
                if (areAllExpanded()) {
                  handleCollapseAll();
                } else {
                  handleExpandAll();
                }
              }}
              className="flex items-center gap-1"
              type="button"
            >
              {areAllExpanded() ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  Collapse All
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  Expand All
                </>
              )}
            </Button>
          )}
        </div>

        {languageChapters.length > 0 ? (
          <div className="border rounded-md p-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={languageChapters.map((ch) => ch.id)}
                strategy={verticalListSortingStrategy}
              >
                {languageChapters.map((chapter) => (
                  <SortableChapterItem
                    key={chapter.id}
                    chapter={chapter}
                    onUpdateChapter={handleUpdateChapter}
                    onRemoveChapter={handleRemoveChapter}
                    isExpanded={expandedIds.includes(chapter.id)}
                    onToggleExpand={toggleChapter}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        ) : (
          <p className="text-muted-foreground text-center border rounded-md py-6">
            No chapters for {selectedLanguageName} yet. Add one below.
          </p>
        )}
      </div>

      <div className="border rounded-md p-4 space-y-4">
        <h3 className="font-medium">Add New Chapter</h3>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="new-chapter-label">Label</Label>
            <Input
              id="new-chapter-label"
              value={newChapter.label}
              onChange={(e) =>
                setNewChapter({ ...newChapter, label: e.target.value })
              }
              placeholder="e.g., Introduction, Chapter 1 of 8"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-chapter-startTime">Start Time (HH:MM:SS)</Label>
            <div className="flex">
              <div className="relative w-full">
                <Clock className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="new-chapter-startTime"
                  type="text"
                  value={newChapterTimeValue}
                  onChange={handleNewTimeChange}
                  onKeyDown={handleNewTimeKeyPress}
                  placeholder="00:00:00"
                  pattern="[0-9]{2}:[0-9]{2}:[0-9]{2}"
                  className="pl-8"
                />
              </div>
              <div className="ml-2 text-xs text-muted-foreground self-center">
                {newChapter.startTime !== undefined &&
                newChapter.startTime !== null
                  ? `${newChapter.startTime}s`
                  : "0s"}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-chapter-title">Title</Label>
          <Input
            id="new-chapter-title"
            value={newChapter.title}
            onChange={(e) =>
              setNewChapter({ ...newChapter, title: e.target.value })
            }
            placeholder="Chapter title"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-chapter-content">Content (Markdown)</Label>
          <div data-color-mode={theme}>
            <MDEditor
              value={newChapter.content || ""}
              onChange={(value) =>
                setNewChapter({ ...newChapter, content: value || "" })
              }
              height={250}
              className={theme === "dark" ? "wmde-markdown-dark" : ""}
            />
          </div>
        </div>

        <Button
          type="button"
          onClick={handleAddChapter}
          disabled={!newChapter.title || !newChapter.content || isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1" /> Add Chapter
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ChapterEditor;
